import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { useToast } from '../../hooks/use-toast';
import { authService } from '../../services/auth';
import { FEATURES } from '../../config/features';

// Form validation schema - only email required for magic link
const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

// Type for magic link form
type MagicLinkFormData = z.infer<typeof loginSchema>;

export function Login() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MagicLinkFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: MagicLinkFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authService.sendMagicLink(data.email);

      console.log('✅ Magic link response:', result);

      // Backend tells us if user exists:
      // - user_exists: false → redirect to waitlist signup
      // - user_exists: true → redirect to magic link sent page
      if (!result.user_exists) {
        console.log('🔀 User does not exist, redirecting to waitlist signup...');
        navigate(`/join-waitlist?email=${encodeURIComponent(data.email)}`);
      } else {
        console.log('✅ Magic link sent, navigating to confirmation page...');
        navigate(`/magic-link-sent?email=${encodeURIComponent(data.email)}`);
      }

      // Don't call setIsLoading(false) - we're navigating away
      return;
    } catch (err) {
      console.log('🔗 Magic link failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send magic link. Please try again.';
      setError(errorMessage);
      setIsLoading(false); // Only set loading false on error
      toast({
        title: 'Failed to send magic link',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleGoogleLogin = () => {
    // TODO: Implement Google OAuth login
    toast({
      title: 'Coming soon',
      description: 'Google login will be available soon.',
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <Link to="/" className="flex justify-center">
            <img
              src="/logo-long.png"
              alt="AccountsGenie"
              className="h-12 object-contain cursor-pointer hover:opacity-80 transition-opacity"
            />
          </Link>
          <CardTitle className="text-2xl font-bold text-center">Welcome back</CardTitle>
          <CardDescription className="text-center">
            Enter your email to receive a magic link to sign in
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                {...register('email')}
                onChange={(e) => {
                  register('email').onChange(e);
                  setError(null);
                }}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && (
                <p id="email-error" className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Sending magic link...' : 'Send Magic Link'}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleLogin}
            >
              <svg
                className="mr-2 h-4 w-4"
                aria-hidden="true"
                focusable="false"
                data-prefix="fab"
                data-icon="google"
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 488 512"
              >
                <path
                  fill="currentColor"
                  d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
                />
              </svg>
              Sign in with Google
            </Button>
          </CardContent>
        </form>
        <CardFooter>
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            {FEATURES.SHOW_SIGNUP && (
              <Link to="/signup" className="text-primary hover:underline">
                Sign up
              </Link>
            )}
            {FEATURES.SHOW_WAITLIST && (
              <Link to="/join-waitlist" className="text-primary hover:underline">
                Join Waitlist
              </Link>
            )}
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}

export default Login;